import { useMemo, type ReactElement } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import {
  Datagrid,
  FunctionField,
  List,
  ReferenceArrayField,
  ReferenceField,
  TextField,
  useCreatePath,
  useGetList,
  useGetRecordId,
  useListContext,
} from "react-admin";
import { useNavigate } from "react-router";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { EmptyState } from "../../misc/EmptyState";
import { CommaSeparatedFieldList } from "../../misc/CommaSeparatedFieldList";
import { VehicleIcon } from "../../icons/VehicleIcon";
import { Vehicle } from "../../providers/vehiclesProvider";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { VehicleMobileListItem } from "../vehicles/VehicleMobileListItem";

export function InstructorVehiclesList() {
  const instructorId = useGetRecordId();
  const createPath = useCreatePath();
  const navigate = useNavigate();
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: instructors } = useGetList("instructors", {
    filter: { drivingSchoolId },
    pagination: { page: 1, perPage: 9999 },
  });
  const otherInstructors = useMemo(
    () => (instructors ?? []).filter((instructor) => instructor.id !== instructorId),
    [instructorId, instructors],
  );
  const otherInstructorIds = otherInstructors.map((instructor) => instructor.id);
  const instructorNamesById = Object.fromEntries(
    otherInstructors.map((instructor) => [instructor.id, instructor.name]),
  );
  const empty = (
    <EmptyState
      noEntriesText="Noch kein Fahrzeug zugewiesen."
      noMatchingEntriesText="Keine passenden Fahrzeuge gefunden."
    />
  );

  return (
    <ResponsiveListSurface variant="compactStandalone" data-testid="InstructorVehiclesList">
      <List
        resource="vehicles"
        filter={{ drivingSchoolId, entitledInstructorIds: instructorId }}
        sort={{ field: "name", order: "ASC" }}
        title=" "
        empty={empty}
        exporter={false}
        actions={false}
      >
        <InstructorVehiclesDataGrid
          empty={empty}
          instructorNamesById={instructorNamesById}
          otherInstructorIds={otherInstructorIds}
          openVehicle={(vehicle) =>
            navigate(createPath({ type: "edit", resource: `${vehicle.type}s`, id: vehicle.id }))
          }
        />
      </List>
    </ResponsiveListSurface>
  );
}

function InstructorVehiclesDataGrid(props: {
  empty: ReactElement;
  instructorNamesById: Record<string, string>;
  otherInstructorIds: string[];
  openVehicle: (vehicle: Vehicle) => void;
}) {
  const { empty, instructorNamesById, otherInstructorIds, openVehicle } = props;
  const { data, isLoading, total } = useListContext<Vehicle>();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const theme = useTheme();

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isMobile) {
    if (!data?.length || total === 0) {
      return empty;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((vehicle) => (
          <VehicleMobileListItem
            key={vehicle.id}
            vehicle={vehicle}
            instructorNamesById={instructorNamesById}
            onClick={() => openVehicle(vehicle)}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick={(_, __, vehicle) => {
        openVehicle(vehicle as Vehicle);
        return false;
      }}
      empty={empty}
    >
      <FunctionField
        label="Typ"
        source="type"
        render={(vehicle: Vehicle) => (
          <VehicleIcon vehicle={vehicle} fontSize="small" htmlColor={theme.palette.text.secondary} />
        )}
      />
      <TextField label="Name" source="name" />
      <TextField label="Notizen" source="notes" />
      <TextField label="Kennzeichen" source="plateNumber" />
      <ReferenceArrayField
        label="Geteilt mit"
        reference="instructors"
        source="entitledInstructorIds"
        filter={{ id: otherInstructorIds }}
      >
        <CommaSeparatedFieldList maxItems={1}>
          <ReferenceField source="id" reference="instructors" className="whitespace-nowrap" />
        </CommaSeparatedFieldList>
      </ReferenceArrayField>
    </Datagrid>
  );
}
